import type React from "react";
import {useEffect, useState} from "react";
import {useParams} from "react-router-dom";

import {faStar} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import {usePlaylist} from "~/api/hooks/usePlaylist";
import {useUpdatePlaylist} from "~/api/hooks/useUpdatePlaylist";
import CustomButton from "~/component/common/CustomButton";

import ButtonSpinner from "../../../../component/spinner/ButtonSpinner";

interface ToggleFeaturedContentProps {
  closeModal: () => void;
}

const ToggleFeaturedContent: React.FC<ToggleFeaturedContentProps> = ({
  closeModal,
}) => {
  const {playlistId} = useParams<{playlistId: string}>();
  const {data: playlist} = usePlaylist({id: playlistId});
  const [isFeatured, setIsFeatured] = useState(false);
  const [featuredRank, setFeaturedRank] = useState(1);

  const {
    mutateAsync: updatePlaylist,
    isPending: isUpdating,
    isSuccess,
    reset,
  } = useUpdatePlaylist();

  // Initialize the state with the current playlist data
  useEffect(() => {
    if (playlist) {
      setIsFeatured(playlist.featured || false);
      setFeaturedRank(playlist.featuredRank || 1);
    }
  }, [playlist]);

  // Close the modal when update is successful
  useEffect(() => {
    if (isSuccess) {
      closeModal();
      reset();
    }
  }, [isSuccess, closeModal, reset]);

  const handleToggleFeatured = () => {
    setIsFeatured(!isFeatured);
  };

  const handleSave = async () => {
    if (!playlist) return;

    try {
      await updatePlaylist({
        data: {
          id: playlist.id,
          featured: isFeatured,
          featuredRank: isFeatured ? featuredRank : undefined,
        },
      });
    } catch (error) {
      console.error("Error updating featured status:", error);
    }
  };

  return (
    <div className="w-full text-white">
      <h2 className="text-2xl mb-4">Featured Status</h2>

      <div className="mb-6">
        <div className="flex items-center mb-4">
          <button
            onClick={handleToggleFeatured}
            className={`flex items-center justify-center w-12 h-6 rounded-full transition-colors ${
              isFeatured ? "bg-yellow-500" : "bg-gray-500"
            }`}
          >
            <div
              className={`w-5 h-5 rounded-full bg-white transform transition-transform ${
                isFeatured ? "translate-x-3" : "-translate-x-3"
              }`}
            />
          </button>
          <span className="ml-3">
            {isFeatured ? "Featured" : "Not Featured"}
          </span>
          <FontAwesomeIcon
            icon={faStar}
            className={`ml-2 ${isFeatured ? "text-yellow-500" : "text-gray-500"}`}
          />
        </div>

        {isFeatured && (
          <div className="mb-4">
            <label className="block mb-2">Featured Rank:</label>
            <input
              type="number"
              min="1"
              value={featuredRank}
              onChange={(e) =>
                setFeaturedRank(
                  Math.max(1, Number.parseInt(e.target.value) || 1),
                )
              }
              className="w-full p-2 border rounded bg-white text-black"
            />
            <p className="text-sm text-gray-400 mt-1">
              Lower numbers appear first in the featured list.
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-4">
        <CustomButton
          className="bg-gray-500 hover:bg-gray-700 text-white py-2 px-4 rounded"
          onClick={closeModal}
        >
          Cancel
        </CustomButton>
        <CustomButton
          className="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded"
          onClick={handleSave}
          disabled={isUpdating}
        >
          <ButtonSpinner isLoading={isUpdating} />
          Save
        </CustomButton>
      </div>
    </div>
  );
};

export default ToggleFeaturedContent;
